import React, { useEffect, useState } from 'react';
import { dataService } from '../../services/dataService';
import { adminService } from '../../services/adminService';

interface Switch {
  key: string;
  label: string;
  description: string;
  fallback: boolean;
}

/** The admin-managed switches (app_settings, migration 014). */
const SWITCHES: Switch[] = [
  {
    key: 'consultation_photos_enabled',
    label: 'Unggah foto konsultasi (FR-17)',
    description:
      'Jika dimatikan, pasien tidak bisa mengunggah foto baru. Foto yang sudah ada tetap ' +
      'bisa dilihat pasien dan dokter.',
    fallback: true
  }
];

/**
 * Superadmin switches. A list, so FR-15's show/hide configuration can add its
 * rows here instead of growing another tab.
 */
const AppSettingsPanel: React.FC<{ adminId: string }> = ({ adminId }) => {
  const [values, setValues] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      SWITCHES.map(async s => [s.key, (await dataService.getAppSetting(s.key, s.fallback)) !== false] as const)
    ).then(entries => {
      if (!cancelled) {
        setValues(Object.fromEntries(entries));
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = async (key: string) => {
    const next = !values[key];
    setBusy(key);
    setError(null);
    try {
      await adminService.setAppSetting(key, next, adminId);
      setValues(prev => ({ ...prev, [key]: next }));
    } catch (err: any) {
      setError(err?.message || 'Gagal menyimpan pengaturan.');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <div className="text-sm">Memuat pengaturan...</div>;
  }

  return (
    <div className="space-y-4">
      {error ? (
        <div className="text-sm text-destructive" role="alert">
          {error}
        </div>
      ) : null}
      {SWITCHES.map(s => (
        <label
          key={s.key}
          className="flex min-h-[44px] items-start gap-3 rounded-md border p-3 text-sm"
        >
          <input
            type="checkbox"
            role="switch"
            className="mt-1"
            checked={!!values[s.key]}
            disabled={busy === s.key}
            onChange={() => toggle(s.key)}
          />
          <span>
            <span className="font-semibold">{s.label}</span>
            <span className="block text-muted-foreground">{s.description}</span>
          </span>
        </label>
      ))}
    </div>
  );
};

export default AppSettingsPanel;
